import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  IconButton,
  Pagination,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemSecondaryAction,
  Menu,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import { useAuth } from '../context/AuthContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const rowsPerPage = 10;
  const { user } = useAuth();
  const [selectedAgents, setSelectedAgents] = useState({});
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [roleEditDialogOpen, setRoleEditDialogOpen] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [agentConfigPermissions, setAgentConfigPermissions] = useState({});
  const [phoneNumbersDialogOpen, setPhoneNumbersDialogOpen] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [newPhoneOwner, setNewPhoneOwner] = useState('');
  const [phoneTransferDialogOpen, setPhoneTransferDialogOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchAgents();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      let usersData = [];
      
      if (Array.isArray(data.users)) {
        usersData = data.users;
      } else if (Array.isArray(data)) {
        usersData = data;
      } else {
        console.error('Received invalid users data format:', data);
        setUsers([]);
        setError('Invalid data format received from server');
        setLoading(false);
        return;
      }
      
      // Since /api/users already includes agents and balance, we don't need individual fetches
      setUsers(usersData);
      setError(null);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Error fetching users: ' + error.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle different response formats
        if (Array.isArray(data)) {
          setAgents(data);
        } else if (data.agents && Array.isArray(data.agents)) {
          setAgents(data.agents);
        } else {
          console.warn('Unexpected API response format for agents:', data);
          setAgents([]);
        }
      } else {
        console.error('Failed to fetch agents:', response.status);
        setAgents([]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    }
  };

  const fetchPhoneNumbers = async () => {
    try {
      setLoadingPhoneNumbers(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/phone-numbers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch phone numbers');
      }
      
      const data = await response.json();
      setPhoneNumbers(data);
    } catch (error) {
      console.error('Error fetching phone numbers:', error);
      showSnackbar('Error fetching phone numbers', 'error');
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) {
      showSnackbar('Email is required', 'error');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ email: newUserEmail })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setNewUserPassword(data.password);
        setOpenPasswordDialog(true);
        setNewUserEmail('');
        fetchUsers();
        
        // Show appropriate message based on email status
        if (data.message.includes('email failed')) {
          showSnackbar('User created but email failed to send. Please ensure to copy the password.', 'warning');
        } else {
          showSnackbar('User created successfully', 'success');
        }
      } else {
        showSnackbar(data.error || 'Failed to create user', 'error');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      showSnackbar('Error creating user', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      // Confirm deletion before proceeding
      if (!window.confirm('Are you sure you want to delete this user?')) {
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        showSnackbar('User deleted successfully', 'success');
        fetchUsers();
      } else {
        showSnackbar(data.error || 'Failed to delete user', 'error');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showSnackbar('Error deleting user', 'error');
    }
  };

  const handleAssignAgents = async (userId) => {
    try {
      console.log(`Assigning agents to user ${userId}, agent IDs:`, selectedAgents[userId]);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userId}/agents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_ids: selectedAgents[userId] || []
        }),
      });
      
      if (response.ok) {
        setMessage('Agents assigned successfully');
        setOpenSnackbar(true);
        
        // Explicitly refresh user data after assignment
        try {
          const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (userResponse.ok) {
            const userData = await userResponse.json();
            
            // Update the specific user in the users array
            setUsers(prevUsers => 
              prevUsers.map(user => 
                user.id === userId ? { ...user, agents: userData.agents || [] } : user
              )
            );
          }
        } catch (error) {
          console.error(`Error fetching updated user data for user ${userId}:`, error);
        }
        
        // Also refresh all users
        fetchUsers();
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to assign agents');
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error(`Error assigning agents to user ${userId}:`, error);
      setMessage('Error assigning agents');
      setOpenSnackbar(true);
    }
  };

  const handleRemoveAgent = async (userId, agentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userId}/agents/${agentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        setMessage('Agent removed successfully');
        setOpenSnackbar(true);
        fetchUsers();
      } else {
        const data = await response.json();
        setMessage(data.error);
        setOpenSnackbar(true);
      }
    } catch (error) {
      setMessage('Error removing agent');
      setOpenSnackbar(true);
    }
  };

  const handleOpenAgentDialog = (user) => {
    console.log("Opening agent dialog for user:", user);
    console.log("User agents:", user.agents);
    
    // Extract agent IDs, carefully handling potential undefined or non-array values
    const agentIds = user.agents && Array.isArray(user.agents) 
      ? user.agents.map(agent => {
          console.log("Agent in user.agents:", agent);
          return agent.id;
        }).filter(Boolean) 
      : [];
    
    console.log("Extracted agent IDs:", agentIds);
    
    // Initialize agent configuration permissions based on existing assignments
    const configPermissions = {};
    if (user.agents && Array.isArray(user.agents)) {
      user.agents.forEach(agent => {
        if (agent.id && agent.user_can_configure !== undefined) {
          configPermissions[agent.id] = agent.user_can_configure;
        }
      });
    }
    
    setSelectedUser(user);
    setSelectedAgentIds(agentIds);
    setAgentConfigPermissions(configPermissions);
    setAgentDialogOpen(true);
  };

  const handleCloseAgentDialog = () => {
    setAgentDialogOpen(false);
    setSelectedUser(null);
    setAgentSearchTerm('');
    setSelectedAgentIds([]);
  };

  const handleSaveAgentAssignments = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${selectedUser.id}/agents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_ids: selectedAgentIds,
          agent_config_permissions: agentConfigPermissions
        }),
      });
      
      if (response.ok) {
        showSnackbar('Agents assigned successfully', 'success');
        
        // Find the selected agents from our agents list with config permissions
        const selectedAgentsData = selectedAgentIds.map(agentId => {
          const agent = agents.find(a => a.id === agentId);
          if (agent) {
            return {
              ...agent,
              user_can_configure: agentConfigPermissions[agentId] || false
            };
          }
          return null;
        }).filter(Boolean);
        
        // Update the users list with the new agent assignments
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === selectedUser.id 
              ? { ...user, agents: selectedAgentsData } 
              : user
          )
        );
        
        // Also fetch users to ensure we have the latest data
        fetchUsers();
        handleCloseAgentDialog();
      } else {
        const data = await response.json();
        showSnackbar(data.error || 'Failed to assign agents', 'error');
      }
    } catch (error) {
      console.error('Error assigning agents:', error);
      showSnackbar('Error assigning agents', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAgents = agents && Array.isArray(agents) ? agents.filter(agent =>
    agent && agent.name && agent.name.toLowerCase().includes(agentSearchTerm.toLowerCase())
  ) : [];

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const renderAssignedAgents = (user) => {
    if (!user || !user.agents || !Array.isArray(user.agents) || user.agents.length === 0) {
      return <Typography variant="body2" color="text.secondary">No agents assigned</Typography>;
    }

    return (
      <Box sx={{ mt: 1 }}>
        {user.agents.map((agent) => (
          <Chip
            key={agent.id}
            label={agent.name || 'Unnamed Agent'}
            size="small"
            sx={{ mr: 0.5, mb: 0.5 }}
          />
        ))}
      </Box>
    );
  };

  const handleOpenRoleEditDialog = (user) => {
    setSelectedUserForRole(user);
    setNewRole(user.role);
    setRoleEditDialogOpen(true);
  };

  const handleCloseRoleEditDialog = () => {
    setRoleEditDialogOpen(false);
    setSelectedUserForRole(null);
    setNewRole('');
  };

  const handleRoleChange = (e) => {
    setNewRole(e.target.value);
  };

  const handleSaveRole = async () => {
    if (!selectedUserForRole || !newRole) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${selectedUserForRole.id}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          role: newRole
        }),
      });
      
      if (response.ok) {
        showSnackbar(`User role updated to ${newRole}`, 'success');
        
        // Update the user in the state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === selectedUserForRole.id ? {...user, role: newRole} : user
          )
        );
        
        handleCloseRoleEditDialog();
        fetchUsers(); // Refresh the user list
      } else {
        const data = await response.json();
        showSnackbar(data.error || 'Failed to update user role', 'error');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      showSnackbar('Error updating user role', 'error');
    }
  };

  const handleOpenPhoneNumbersDialog = () => {
    setPhoneNumbersDialogOpen(true);
    fetchPhoneNumbers();
  };

  const handleClosePhoneNumbersDialog = () => {
    setPhoneNumbersDialogOpen(false);
  };

  const handleOpenPhoneTransferDialog = (phoneNumber) => {
    setSelectedPhoneNumber(phoneNumber);
    setNewPhoneOwner(phoneNumber.owner_id || '');
    setPhoneTransferDialogOpen(true);
  };

  const handleClosePhoneTransferDialog = () => {
    setPhoneTransferDialogOpen(false);
    setSelectedPhoneNumber(null);
    setNewPhoneOwner('');
  };

  const handlePhoneOwnerChange = (e) => {
    setNewPhoneOwner(e.target.value);
  };

  const handleTransferPhoneNumber = async (phoneId, newUserId) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/phone-numbers/${phoneId}/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ user_id: newUserId })
      });

      if (response.ok) {
        showSnackbar('Phone number transferred successfully', 'success');
        fetchPhoneNumbers();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to transfer phone number');
      }
    } catch (error) {
      console.error('Error transferring phone number:', error);
      showSnackbar(error.message || 'Failed to transfer phone number', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          User Management
        </Typography>
        
        <Box>
          {user?.role === 'admin' && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PhoneIcon />}
              onClick={handleOpenPhoneNumbersDialog}
              sx={{ ml: 1 }}
            >
              Manage Phone Numbers
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Add a debug info section for development troubleshooting */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mb: 3, p: 2, border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="warning.main" gutterBottom>
            Debug Information
          </Typography>
          <Typography variant="body2">
            Users loaded: {users.length}
          </Typography>
          <Typography variant="body2">
            Agents loaded: {agents.length}
        </Typography>
          <Box sx={{ mt: 1 }}>
            <Button 
              size="small" 
              variant="outlined" 
              color="warning" 
              onClick={() => {
                console.log("Current users:", users);
                console.log("Current agents:", agents);
                console.log("Filtered users:", filteredUsers);
              }}
            >
              Log Data to Console
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              color="warning" 
              onClick={fetchUsers}
              sx={{ ml: 1 }}
            >
              Refresh Users
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              color="warning" 
              onClick={fetchAgents}
              sx={{ ml: 1 }}
            >
              Refresh Agents
            </Button>
          </Box>
        </Box>
      )}

      <Box component="form" onSubmit={handleAddUser} sx={{ mb: 4 }}>
        <TextField
          fullWidth
          label="New User Email"
          value={newUserEmail}
          onChange={(e) => setNewUserEmail(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Button type="submit" variant="contained" color="primary">
          Add User
        </Button>
      </Box>

      {message && (
        <Typography color="primary" sx={{ mb: 2 }}>
          {message}
        </Typography>
      )}

      <TextField
        fullWidth
        label="Search Users"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />

      <List>
        {Array.isArray(filteredUsers) && filteredUsers.length > 0 ? filteredUsers.map((user) => {
          console.log(`Rendering user ${user.id}:`, user);
          console.log(`User ${user.id} agents:`, user.agents);
          
          return (
          <ListItem 
            key={user.id}
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              borderBottom: '1px solid',
              borderColor: 'divider',
              py: 2
            }}
          >
            <Box sx={{ 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box>
                <ListItemText 
                  primary={user.email} 
                  secondary={
                    <React.Fragment>
                      <Typography component="span" variant="body2" color="text.primary">
                        {user.role}
                      </Typography>
                      {user.balance !== undefined && (
                        <Typography component="span" variant="body2" color="text.primary" sx={{ ml: 2 }}>
                          Balance: {user.role === 'admin' ? 'Unlimited' : `$${typeof user.balance === 'number' ? user.balance.toFixed(2) : '0.00'}`}
                        </Typography>
                      )}
                    </React.Fragment>
                  }
                />
                  {/* Display user's assigned agents */}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Assigned Agents:
                    </Typography>
                    {user.agents && Array.isArray(user.agents) && user.agents.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.agents.map((agent) => (
                          <Chip
                            key={agent.id || `user-${user.id}-agent-${Math.random()}`}
                            label={agent.name || 'Unnamed Agent'}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No agents assigned</Typography>
                    )}
                  </Box>
              </Box>
              {user.role !== 'admin' && (
                <Box>
                  <IconButton
                    onClick={() => handleOpenRoleEditDialog(user)}
                    sx={{ mr: 1 }}
                    aria-label="edit role"
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleOpenAgentDialog(user)}
                    sx={{ mr: 1 }}
                    aria-label="assign agents"
                    color="primary"
                  >
                    <PersonAddIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDeleteUser(user.id)}
                    aria-label="delete"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </ListItem>
          );
        }) : (
          <Typography variant="body1" sx={{ py: 2 }}>No users found</Typography>
        )}
      </List>

      <Dialog 
        open={agentDialogOpen} 
        onClose={handleCloseAgentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Agents to {selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search agents..."
              value={agentSearchTerm}
              onChange={(e) => setAgentSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }}
            />
          </Box>
          
          {/* Display currently assigned agents */}
          {selectedUser && selectedUser.agents && Array.isArray(selectedUser.agents) && selectedUser.agents.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Currently Assigned:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedUser.agents.map((agent) => (
                <Chip
                    key={agent.id || `existing-${Math.random()}`}
                    label={agent.name || 'Unnamed Agent'}
                  size="small"
                    color="primary"
                    variant="outlined"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
              </Box>
            </Box>
          )}
          
          {/* Container for agent selection */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Select Agents:
          </Typography>
          
          <Box sx={{ 
            maxHeight: 300, 
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1
          }}>
            {/* Display loading spinner while agents are being loaded */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : Array.isArray(agents) && agents.length > 0 ? (
              agents
              .filter(agent => 
                  agent && agent.name && agent.name.toLowerCase().includes(agentSearchTerm.toLowerCase())
              )
              .map((agent) => (
                <Box 
                    key={agent.id || `agent-${Math.random()}`}
                  sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': { bgcolor: 'action.hover' },
                    cursor: 'pointer',
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: selectedAgentIds.includes(agent.id) ? 'action.selected' : 'transparent'
                  }}
                  onClick={() => {
                    setSelectedAgentIds(prev => 
                      prev.includes(agent.id)
                        ? prev.filter(id => id !== agent.id)
                        : [...prev, agent.id]
                    );
                  }}
                >
                  <Checkbox
                    checked={selectedAgentIds.includes(agent.id)}
                    onChange={() => {}}
                      color="primary"
                    />
                    <Typography sx={{ flexGrow: 1 }}>{agent.name || 'Unnamed Agent'}</Typography>
                    
                    {/* Only show configuration permission checkbox if agent is selected */}
                    {selectedAgentIds.includes(agent.id) && (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid rgba(25, 118, 210, 0.2)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="When enabled, this user will be able to modify and configure this agent's settings. If disabled, they can only use the agent but not change its settings.">
                          <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold', color: 'primary.main' }}>
                            Allow Configure:
                          </Typography>
                        </Tooltip>
                        <Checkbox
                          checked={!!agentConfigPermissions[agent.id]}
                          onChange={(e) => {
                            setAgentConfigPermissions(prev => ({
                              ...prev,
                              [agent.id]: e.target.checked
                            }));
                          }}
                          size="small"
                          color="primary"
                        />
                      </Box>
                    )}
                  </Box>
                ))
            ) : (
              <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                No agents available
              </Typography>
            )}
                </Box>
          
          {/* Show count of selected agents */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {selectedAgentIds.length} agent(s) selected
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAgentDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveAgentAssignments}
            variant="contained"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination 
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
        />
      </Box>

      <Dialog 
        open={openPasswordDialog} 
        onClose={() => setOpenPasswordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ mb: 3 }}>
            <CheckCircleIcon sx={{ 
              fontSize: 48, 
              color: 'success.main',
              mb: 2 
            }} />
          </Box>
          <Typography variant="h6" component="div" sx={{ mb: 3 }}>
            User created successfully!
          </Typography>
          <Box sx={{
            backgroundColor: '#f5f5f7',
            borderRadius: 2,
            p: 3,
            mb: 3,
            mx: 'auto',
            maxWidth: '400px'
          }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Generated Password:
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: '#0066FF',
                backgroundColor: 'white',
                py: 2,
                px: 3,
                borderRadius: 1,
                border: '2px solid #e1e1e5'
              }}
            >
              {newUserPassword}
            </Typography>
          </Box>
          <Typography 
            variant="body2" 
            color="warning.main"
            sx={{ fontWeight: 500 }}
          >
            Please save this password - it won't be shown again!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <Button 
            onClick={() => setOpenPasswordDialog(false)}
            variant="contained"
            sx={{
              minWidth: 120,
              backgroundColor: '#0066FF',
              '&:hover': {
                backgroundColor: '#0052CC'
              }
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={roleEditDialogOpen} 
        onClose={handleCloseRoleEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit User Role
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>User Role</InputLabel>
              <Select
                value={newRole}
                onChange={handleRoleChange}
                label="User Role"
              >
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="agency">Agency</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {newRole === 'agency' 
                ? 'Agency accounts can manage client accounts and have more privileges.' 
                : 'Client accounts are standard user accounts with basic privileges.'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRoleEditDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveRole}
            variant="contained"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phone Numbers Management Dialog */}
      <Dialog
        open={phoneNumbersDialogOpen}
        onClose={handleClosePhoneNumbersDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Phone Numbers Management</DialogTitle>
        <DialogContent>
          {loadingPhoneNumbers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : phoneNumbers.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Friendly Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {phoneNumbers.map((phone) => (
                    <TableRow key={phone.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                          {phone.phone_number || phone.phoneNumber}
                        </Box>
                      </TableCell>
                      <TableCell>{phone.friendly_name || phone.friendlyName}</TableCell>
                      <TableCell>{phone.status}</TableCell>
                      <TableCell>{phone.owner_email || 'Unknown'}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenPhoneTransferDialog(phone)}
                        >
                          Transfer
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 5 }}>
              No phone numbers found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhoneNumbersDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Phone Transfer Dialog */}
      <Dialog
        open={phoneTransferDialogOpen}
        onClose={handleClosePhoneTransferDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Transfer Phone Number</DialogTitle>
        <DialogContent>
          {selectedPhoneNumber && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Phone Number: {selectedPhoneNumber.phone_number || selectedPhoneNumber.phoneNumber}
              </Typography>
              <Typography variant="body2" paragraph>
                Current Owner: {selectedPhoneNumber.owner_email || 'Unknown'}
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="new-owner-label">New Owner</InputLabel>
                <Select
                  labelId="new-owner-label"
                  value={newPhoneOwner}
                  label="New Owner"
                  onChange={handlePhoneOwnerChange}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhoneTransferDialog}>Cancel</Button>
          <Button 
            onClick={() => handleTransferPhoneNumber(selectedPhoneNumber.id, newPhoneOwner)}
            variant="contained"
            color="primary"
            disabled={!newPhoneOwner}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 